/**
 * Authentication utility functions
 */

import type { DecodedIdToken, DecodedAccessToken } from '@/types/auth';

/**
 * Decode JWT token payload (browser-compatible)
 */
export function decodeToken<T = unknown>(token: string): T {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid token format');
  }

  try {
    // Use atob for browser compatibility (instead of Buffer.from)
    const payload = atob(parts[1]);
    return JSON.parse(payload) as T;
  } catch {
    throw new Error('Failed to decode token');
  }
}

/**
 * Check if a token is expired
 */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = decodeToken<{ exp: number }>(token);
    if (!decoded.exp) {
      return true;
    }
    // Add 30 second buffer to refresh before actual expiration
    return Date.now() >= (decoded.exp * 1000 - 30000);
  } catch {
    return true;
  }
}

/**
 * Get user info from ID token
 */
export function getUserFromIdToken(idToken: string) {
  const decoded = decodeToken<DecodedIdToken>(idToken);
  return {
    id: decoded.sub,
    email: decoded.email,
    name: decoded.email.split('@')[0], // Use email prefix as name
  };
}

/**
 * Get token expiration time in milliseconds
 */
export function getTokenExpiration(token: string): number {
  try {
    const decoded = decodeToken<DecodedAccessToken>(token);
    return decoded.exp * 1000;
  } catch {
    return 0;
  }
}
